import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CircleHelpIcon, XIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Button } from '@/components/ui/button';
import { Autocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useLanguageModels from '@/modules/developerTools/hooks/useLanguageModels';
import { ProviderName } from '@/modules/developerTools/types';
import { randId } from '@/redux/dialogs/helper';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { selectSidebar } from '@/redux/sidebar/selectors';
import { RootState } from '@/shared/types/state';
import { isApiKeyMasked } from '@/util/constants';
import { LENGTH_XL } from '@/util/validation/constants';

import {
  getDefaultReasoningEffort,
  getReasoningEffortOptions,
  isGpt5Model,
  MAX_MODEL_NAME,
  modelIconResolver,
  providerNameMapper,
} from './helper';

interface FormValues {
  apikey: string;
  model: string;
  provider: string;
  deploymentId: string;
  instanceName: string;
  reasoningEffort: string;
}

const SELECTABLE_PROVIDERS: ProviderName[] = ['openai', 'anthropic', 'azure', 'gemini', 'deepseek'];

export const ConnectLanguageModelPanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sidebarMeta = useSelector((state: RootState) => selectSidebar(state).meta);
  const languageModelId = sidebarMeta?.languageModelId;

  const isEditing = !!languageModelId;
  const initialProvider = (sidebarMeta?.provider as ProviderName) ?? 'openai';

  const schema = useMemo(
    () =>
      yup.object().shape({
        provider: yup.string().required(t('validation.required')),
        apikey: yup
          .string()
          .required(t('validation.required'))
          .max(LENGTH_XL, t('validation.less_than', { 0: LENGTH_XL })),
        model: yup.string().required(t('validation.required')),
        deploymentId: yup
          .string()
          .when('provider', {
            is: 'azure',
            then: (s) => s.required(t('validation.required')),
            otherwise: (s) => s.notRequired(),
          })
          .max(LENGTH_XL, t('validation.less_than', { 0: LENGTH_XL })),
        instanceName: yup
          .string()
          .when('provider', {
            is: 'azure',
            then: (s) => s.required(t('validation.required')),
            otherwise: (s) => s.notRequired(),
          })
          .max(LENGTH_XL, t('validation.less_than', { 0: LENGTH_XL })),
        reasoningEffort: yup
          .string()
          .when(['provider', 'model'], {
            is: (provider: string, model: string) =>
              (provider === 'openai' || provider === 'azure') && model?.startsWith('gpt-5'),
            then: (s) => s.oneOf(['', 'none', 'minimal', 'low', 'medium', 'high']),
            otherwise: (s) => s.notRequired(),
          }),
      }),
    [t]
  );

  const form = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      apikey: '',
      model: '',
      deploymentId: '',
      instanceName: '',
      reasoningEffort: '',
      provider: initialProvider,
    },
    resolver: yupResolver(schema) as Resolver<FormValues>,
  });

  const { getValues, setValue, reset } = form;

  const watchedProvider = useWatch({ control: form.control, name: 'provider' }) as ProviderName;
  const watchedModel = useWatch({ control: form.control, name: 'model' });
  const watchedApikey = useWatch({ control: form.control, name: 'apikey' });

  const {
    providers,
    createLanguageModel,
    createStatus,
    languageModel,
    updateLanguageModel,
    updateStatus,
    languageModels,
    apiKeys,
  } = useLanguageModels({ languageModelId, provider: watchedProvider });

  // Populate form with existing data when editing
  useEffect(() => {
    if (isEditing && languageModel) {
      reset({
        apikey: languageModel.apikey || '',
        model: languageModel.model || '',
        deploymentId: languageModel.config?.deployment || '',
        instanceName: languageModel.config?.resource || '',
        reasoningEffort:
          languageModel.config?.reasoning_effort ||
          getDefaultReasoningEffort(languageModel.model || ''),
        provider: languageModel.provider,
      });
    }
  }, [isEditing, languageModel, reset]);

  const providerOptions = useMemo(() => {
    return SELECTABLE_PROVIDERS.filter((p) => providers?.[p]).map((p) => {
      const connectedModels = new Set(
        (languageModels ?? []).filter((lm) => lm.provider === p).map((lm) => lm.model)
      );
      const availableCount =
        providers?.[p]?.models?.filter((m) => !connectedModels.has(m.name)).length || 0;

      return { value: p, label: providerNameMapper(p), modelCount: availableCount };
    });
  }, [providers, languageModels]);

  const apiKeyOptions = useMemo(() => {
    return apiKeys.map((keyObj) => ({
      value: keyObj.apikey,
      label: keyObj.apikey,
      id: keyObj.source_language_model_id,
    }));
  }, [apiKeys]);

  const modelOptions = useMemo(() => {
    const allModels =
      providers?.[watchedProvider]?.models.map((model) => ({
        label: model.name,
        value: model.name,
      })) || [];

    if (isEditing) {
      return allModels;
    }

    const existingModelsSet = new Set(
      (languageModels ?? []).filter((lm) => lm.provider === watchedProvider).map((lm) => lm.model)
    );

    return allModels.filter((m) => !existingModelsSet.has(m.value));
  }, [isEditing, languageModels, providers, watchedProvider]);

  const showReasoningEffort =
    (watchedProvider === 'openai' || watchedProvider === 'azure') && isGpt5Model(watchedModel);

  const reasoningEffortOptions = useMemo(
    () => getReasoningEffortOptions(watchedModel),
    [watchedModel]
  );

  const effortLabels = useMemo<Record<string, string>>(
    () => ({
      none: t('developer_tools.reasoning_effort_options.none'),
      minimal: t('developer_tools.reasoning_effort_options.minimal'),
      low: t('developer_tools.reasoning_effort_options.low'),
      medium: t('developer_tools.reasoning_effort_options.medium'),
      high: t('developer_tools.reasoning_effort_options.high'),
    }),
    [t]
  );

  useEffect(() => {
    if (showReasoningEffort) {
      const currentEffort = getValues('reasoningEffort');
      if (
        !isEditing ||
        !currentEffort ||
        !(reasoningEffortOptions as readonly string[]).includes(currentEffort)
      ) {
        setValue('reasoningEffort', getDefaultReasoningEffort(watchedModel), { shouldDirty: true });
      }
    }
  }, [watchedModel, showReasoningEffort, setValue, getValues, isEditing, reasoningEffortOptions]);

  const handleClose = useCallback(() => {
    dispatch(setSidebarContent(null));
  }, [dispatch]);

  const handleProviderChange = useCallback(
    (newProvider: string) => {
      setValue('provider', newProvider as ProviderName, { shouldDirty: true });
      setValue('model', '');
      setValue('apikey', '');
      setValue('deploymentId', '');
      setValue('instanceName', '');
      setValue('reasoningEffort', '');
    },
    [setValue]
  );

  const onSubmit = useCallback(
    (data: FormValues) => {
      const sourceLanguageModelId = apiKeyOptions.find(
        (option) => option.value === data.apikey
      )?.id;

      const cleanData = isApiKeyMasked(data.apikey)
        ? (({ apikey: _, ...rest }) => rest)(data)
        : data;

      let config: Record<string, unknown> | undefined;
      if (data.provider === 'anthropic') {
        const maxTokens = providers?.anthropic?.models.find(
          (model) => model.name === data.model
        )?.max_tokens;
        config = { max_tokens: maxTokens };
      } else if (data.provider === 'azure') {
        config = { deployment: data.deploymentId, resource: data.instanceName };
      }

      if (
        (data.provider === 'openai' || data.provider === 'azure') &&
        isGpt5Model(data.model) &&
        data.reasoningEffort
      ) {
        config = { ...config, reasoning_effort: data.reasoningEffort };
      }

      const shared = {
        ...cleanData,
        provider: data.provider as ProviderName,
        source_language_model_id: sourceLanguageModelId,
        ...(config && { config }),
      };

      if (isEditing && languageModelId) {
        updateLanguageModel(
          { ...shared, language_model_id: languageModelId },
          {
            onSuccess: () => {
              dispatch(setSidebarContent(null));
            },
          }
        );
      } else {
        const modelName =
          `${data.provider}-${cleanData.model}`.slice(0, MAX_MODEL_NAME) + `-${randId(4)}`;

        createLanguageModel(
          { ...shared, name: modelName, enabled: true },
          {
            onSuccess: () => {
              dispatch(setSidebarContent(null));
            },
          }
        );
      }
    },
    [
      apiKeyOptions,
      createLanguageModel,
      dispatch,
      isEditing,
      languageModelId,
      providers?.anthropic?.models,
      updateLanguageModel,
    ]
  );

  const isPending = createStatus === 'pending' || updateStatus === 'pending';
  const isSubmitDisabled = (isEditing && !form.formState.isDirty) || isPending;

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle className="pr-4 pl-6">
        <span className="text-base font-medium text-foreground">
          {isEditing ? t('developer_tools.edit_llm_title') : t('developer_tools.connect_llm_title')}
        </span>
        <Button variant="ghost-neutral" size="icon-sm" onClick={handleClose}>
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>

      <Form {...form}>
        <SidebarWrapperForm onSubmit={form.handleSubmit(onSubmit)}>
          <SidebarWrapperContent className="px-1 pb-1">
            <FormField
              control={form.control}
              name="provider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('developer_tools.language_models_provider')}</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={handleProviderChange}
                      disabled={isEditing}
                    >
                      <SelectTrigger className="w-full">
                        {watchedProvider ? (
                          <span className="flex items-center gap-2">
                            {modelIconResolver(watchedProvider, 16)}
                            <span className="truncate">{providerNameMapper(watchedProvider)}</span>
                          </span>
                        ) : (
                          <span>{t('common.select_dots')}</span>
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {providerOptions.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            textValue={option.label}
                          >
                            <div className="flex items-center gap-2">
                              {modelIconResolver(option.value as ProviderName, 32)}
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold text-foreground">
                                  {option.label}
                                </span>
                                <span className="text-xs text-foreground-muted">
                                  {t('developer_tools.available_models', { 0: option.modelCount })}
                                </span>
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="apikey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('developer_tools.sidebar_api_key')}</FormLabel>
                  <FormControl>
                    {apiKeyOptions.length > 0 ? (
                      <Autocomplete
                        options={apiKeyOptions}
                        value={watchedApikey}
                        onChange={(value) =>
                          setValue('apikey', value, { shouldDirty: true, shouldValidate: true })
                        }
                        placeholder={t('developer_tools.select_or_type_api_key')}
                        searchPlaceholder={t('developer_tools.type_api_key')}
                        onCreateNew={(value) =>
                          setValue('apikey', value, { shouldDirty: true, shouldValidate: true })
                        }
                        createNewPlaceholder={t('developer_tools.sidebar_api_key')}
                      />
                    ) : (
                      <Input {...field} placeholder={t('developer_tools.type_api_key')} />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="model"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('developer_tools.language_models_model')}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange} disabled={isEditing}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t('common.select_dots')} />
                      </SelectTrigger>
                      <SelectContent>
                        {modelOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchedProvider === 'azure' && (
              <>
                <FormField
                  control={form.control}
                  name="deploymentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('developer_tools.deployment_id')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="instanceName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('developer_tools.instance_name')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {showReasoningEffort && (
              <FormField
                control={form.control}
                name="reasoningEffort"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('developer_tools.reasoning_effort')}
                      <InfoTooltip
                        content={t('developer_tools.reasoning_effort_tooltip')}
                        side="right"
                        size="large"
                      >
                        <CircleHelpIcon size={12} className="text-foreground-muted" />
                      </InfoTooltip>
                    </FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('common.select_dots')} />
                        </SelectTrigger>
                        <SelectContent>
                          {reasoningEffortOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {effortLabels[option]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </SidebarWrapperContent>

          <SidebarWrapperFooter>
            <Button type="submit" disabled={isSubmitDisabled} isLoading={isPending}>
              {isEditing ? t('common.save') : t('common.done')}
            </Button>
            <Button type="button" variant="ghost-neutral" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
          </SidebarWrapperFooter>
        </SidebarWrapperForm>
      </Form>
    </SidebarWrapper>
  );
};
